import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Translator from 'translator';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withStyles, Grid } from '@material-ui/core';
import { FHEContainer } from 'data/components';
import { Toastr, tools } from 'data/common';
import renderEnvComponent from 'data/common/renderEnvComponent';
import UserCard from '../components/UserCard';
import {
    addSpecialHomeUser,
    getHomeUsers,
    deleteHomeUser,
    addHomeUser,
    editHomeUser,
    resetPassword,
    switchHomeMaster,
    deleteHomeUserAndSwitchMaster,
    resendWelcomeUserEmail
} from '../../../data/Homes/actions';
import getSelectors from '../selectors';

class HomeUsers extends PureComponent {
    static propTypes = {
        addSpecialHomeUser: PropTypes.func.isRequired,
        switchHomeMaster: PropTypes.func.isRequired,
        deleteHomeUserAndSwitchMaster: PropTypes.func.isRequired,
        resendWelcomeUserEmail: PropTypes.func.isRequired,
        deleteHomeUser: PropTypes.func.isRequired,
        getHomeUsers: PropTypes.func.isRequired,
        addHomeUser: PropTypes.func.isRequired,
        resetPassword: PropTypes.func.isRequired,
        editHomeUser: PropTypes.func.isRequired,
        homeId: PropTypes.string.isRequired,
        profile: PropTypes.object.isRequired,
        history: PropTypes.object.isRequired,
        classes: PropTypes.object.isRequired,
        homeUsers: PropTypes.object.isRequired,
        isActive: PropTypes.bool
    };

    static defaultProps = {
        isActive: true
    };

    constructor(props) {
        super(props);
        this.state = {
            actionDisabled: false,
            loading: props.isActive,
            needGetData: props.isActive,
            fetching: false,
            initialized: false,
            error: null
        };
    }

    static getDerivedStateFromProps(props, state) {
        const { needGetData, loading, error, fetching, initialized } = state;
        const { isActive } = props;

        if (isActive && !needGetData && !loading && !error && !fetching && !initialized) return { needGetData: true, loading: true };
        return null;
    }

    componentDidMount() {
        const { isActive } = this.props;
        if (isActive) this.getHomeUsers();
    }

    componentDidUpdate() {
        const { isActive } = this.props;
        const { needGetData, fetching, error } = this.state;

        if (isActive && needGetData && !fetching && !error) {
            this.getHomeUsers();
        }
    }

    getHomeUsers = () => {
        const { getHomeUsers, homeId } = this.props;
        this.state.fetching = true;
        getHomeUsers(homeId)
            .then(() => {
                this.setState({
                    error: null,
                    loading: false,
                    fetching: false,
                    needGetData: false,
                    initialized: true
                });
            })
            .catch(err => {
                const message = Translator.translateWithFallback(`api.message.${err.message ? err.message : err}`, 'api.message.default');
                Toastr.error(message);
                this.setState({ error: message, loading: false, needGetData: false, fetching: false });
            });
    };

    retryGetData = () => {
        this.setState({ error: null, loading: true, needGetData: true });
    };

    toggleStateActionDisabled = state => {
        this.setState({ actionDisabled: state });
    };

    addHomeUser = userForm => {
        const { addHomeUser, homeId, history } = this.props;

        return addHomeUser(homeId, userForm)
            .then(() => {
                Toastr.success(Translator.translate(`generic.resident.actions.add.success`, userForm.lastname, userForm.firstname));
            })
            .catch(err => {
                Toastr.error(Translator.translate(`generic.resident.actions.add.error`, userForm.lastname, userForm.firstname));
                throw err;
            })
            .finally(() => {
                this.toggleStateActionDisabled(false);
            });
    };

    addSpecialHomeUser = userForm => {
        const { addSpecialHomeUser, homeId } = this.props;

        return addSpecialHomeUser(homeId, userForm)
            .then(() => {
                Toastr.success(Translator.translate(`generic.resident.actions.add.success`, userForm.lastname, userForm.firstname));
            })
            .catch(err => {
                Toastr.error(Translator.translate(`generic.resident.actions.add.error`, userForm.lastname, userForm.firstname));
                throw err;
            })
            .finally(() => {
                this.toggleStateActionDisabled(false);
            });
    };

    editHomeUser = (userId, userForm) => {
        const { editHomeUser, homeId, history } = this.props;

        return editHomeUser(homeId, userId, userForm)
            .then(() => {
                Toastr.success(Translator.translate(`generic.resident.actions.edit.success`, userForm.lastname, userForm.firstname));
            })
            .catch(err => {
                Toastr.error(Translator.translate(`generic.resident.actions.edit.error`, userForm.lastname, userForm.firstname));
                throw err;
            })
            .finally(() => {
                this.toggleStateActionDisabled(false);
            });
    };

    deleteHomeUser = userId => {
        const { deleteHomeUser, homeId, history } = this.props;

        return deleteHomeUser(homeId, userId)
            .then(() => {
                Toastr.success(Translator.translate(`generic.resident.actions.delete.success`));
            })
            .catch(err => {
                Toastr.error(Translator.translate(`generic.resident.actions.delete.error`), err);
                throw err;
            })
            .finally(() => {
                this.toggleStateActionDisabled(false);
            });
    };

    deleteHomeUserAndSwitchMaster = (userId, masterId) => {
        const { deleteHomeUserAndSwitchMaster, homeId, history } = this.props;

        return deleteHomeUserAndSwitchMaster(homeId, userId, masterId)
            .then(() => {
                Toastr.success(Translator.translate(`generic.resident.actions.delete.success`));
            })
            .catch(err => {
                Toastr.error(Translator.translate(`generic.resident.actions.delete.error`), err);
                throw err;
            })
            .finally(() => {
                this.toggleStateActionDisabled(false);
            });
    };

    switchHomeMaster = masterId => {
        const { switchHomeMaster, homeId, history } = this.props;
        return switchHomeMaster(homeId, masterId)
            .then(() => {
                Toastr.success(Translator.translate(`generic.resident.actions.switchMaster.success`));
            })
            .catch(err => {
                Toastr.error(Translator.translate(`generic.resident.actions.switchMaster.error`), err);
                throw err;
            })
            .finally(() => {
                this.toggleStateActionDisabled(false);
            });
    };

    resetPassword = userId => {
        const { resetPassword, homeId } = this.props;
        return resetPassword(userId, homeId)
            .then(data => {
                return data;
            })
            .catch(err => {
                throw err;
            })
            .finally(() => {
                this.toggleStateActionDisabled(false);
            });
    };

    resendWelcomeUserEmail = userId => {
        const { resendWelcomeUserEmail, homeId } = this.props;
        return resendWelcomeUserEmail(userId, homeId)
            .then(data => {
                return data;
            })
            .catch(err => {
                throw err;
            })
            .finally(() => {
                this.toggleStateActionDisabled(false);
            });
    };

    renderUserList = () => {
        const { actionDisabled } = this.state;
        const { homeUsers, profile, history } = this.props;

        const userRights = profile.getIn(['user_rights', history.environment], []);
        const users = [];
        let setIsMaster = true;
        homeUsers.map((user, userId) => {
            let otherUsers = [];
            if (user.get('isMaster')) {
                setIsMaster = false;
                homeUsers.map((_user, _userId) => {
                    if (userId !== _userId)
                        otherUsers.push({
                            id: _userId,
                            label: `${_user.get('firstname')} ${_user.get('lastname')}`
                        });
                });
            }
            users.push(
                <Grid key={userId} container item sm={12} md={6} xl={4}>
                    <UserCard
                        user={user}
                        userId={parseInt(userId)}
                        editUserFunc={this.editHomeUser}
                        deleteUserFunc={this.deleteHomeUser}
                        deleteUserAndSwitchMasterFunc={this.deleteHomeUserAndSwitchMaster}
                        switchMasterFunc={this.switchHomeMaster}
                        resetPasswordFunc={this.resetPassword}
                        resendWelcomeUserEmailFunc={this.resendWelcomeUserEmail}
                        actionDisabled={actionDisabled}
                        resetUserPasswordRight={profile.get('admin') || userRights.includes(`homeResetUserPassword`)}
                        resendWelcomeUserEmailRight={profile.get('admin') || userRights.includes(`homeResendUserWelcomeEmail`)}
                        deleteUserRight={profile.get('admin') || userRights.includes(`homeDeleteUser`)}
                        switchMasterRight={profile.get('admin') || userRights.includes(`homeSwitchMaster`)}
                        deleteMasterRight={profile.get('admin') || userRights.includes(`homeDeleteMaster`)}
                        editUserRight={profile.get('admin') || userRights.includes(`homeEditUser`)}
                        toggleActions={this.toggleStateActionDisabled}
                        otherUsers={otherUsers}
                    />
                </Grid>
            );
        });
        if (profile.get('admin') || userRights.includes('homeAddUser') || userRights.includes(`homeAddSpecialUser_${history.environment}`)) {
            users.push(
                <Grid key="cardAdd" container item md={12} lg={6} xl={4}>
                    <UserCard modeAdd addUserFunc={this.addHomeUser} setIsMaster={setIsMaster} addSpecialUserFunc={this.addSpecialHomeUser} />
                </Grid>
            );
        }

        if (users.length === 0) return <div>{Translator.translate(`generic.resident.table.body.emptyDataSourceMessage`)}</div>;
        return (
            <Grid container spacing={2} justify={users.length === 1 ? 'center' : 'flex-start'}>
                {users}
            </Grid>
        );
    };
    render() {
        const { error, loading } = this.state;
        const { classes } = this.props;

        return (
            <FHEContainer
                hasNoClassContainer
                loading={loading}
                error={error}
                classContainer={classes.container}
                onClickFunc={this.retryGetData}
                onRender={this.renderUserList}
            />
        );
    }
}

function mapStateToProps(state, props) {
    const { history, match } = props;
    const homeId = match.params.homeId;
    const selectors = getSelectors(history.environment);
    return {
        profile: selectors.getProfile(state),
        homeUsers: selectors.getUsersByHomeId(state, homeId).sortBy(user => (user.get('isMaster') ? -1 : 1)),
        homeId
    };
}

const styles = () => ({
    container: {
        margin: 8
    },
    loadingContainer: {
        textAlign: 'center',
        margin: 20
    }
});

export default withRouter(
    renderEnvComponent(
        'scenes/Home/sections/HomeUsers',
        connect(
            mapStateToProps,
            (dispatch, ownProps) =>
                bindActionCreators(
                    tools.mergePropsToAction(ownProps.history.environment, {
                        getHomeUsers,
                        deleteHomeUser,
                        addHomeUser,
                        editHomeUser,
                        resetPassword,
                        switchHomeMaster,
                        deleteHomeUserAndSwitchMaster,
                        resendWelcomeUserEmail,
                        addSpecialHomeUser
                    }),
                    dispatch
                )
        )(withStyles(styles, { withTheme: true })(HomeUsers))
    )
);



// WEBPACK FOOTER //
// ./environments/genericEnv/scenes/Home/sections/HomeUsers.js